/**
 * Finish an eptai-control login when this browser is already signed in.
 *
 * mcp-tools sends the browser here with a transaction id. This page posts the
 * current ID token, then opens the finish URL on mcp-tools. mcp-tools is the
 * one that redirects to the client's callback.
 */

const _ISSUER_BY_HOST: Record<string, string> = {
  'app.ept.ai': 'https://mcp.ept.ai/v1/mcp-tools/eptai-control',
  'app.dev-ept-ai.com': 'https://mcp.dev-ept-ai.com/v1/mcp-tools/eptai-control',
  'app.stage-ept-ai.com': 'https://mcp.stage-ept-ai.com/v1/mcp-tools/eptai-control',
};

export type McpControlLogin = {
  txn: string;
  issuer: string;
};

/** The mcp-tools issuer this app host is allowed to finish a login with. */
export function controlIssuerForHost(hostname: string): string | null {
  return _ISSUER_BY_HOST[hostname] ?? null;
}

/**
 * Read the transaction mcp-tools put on the login URL.
 *
 * Example input: `?mcp_txn=abc&iss=https://mcp.ept.ai/v1/mcp-tools/eptai-control`
 * and that same issuer as `allowedIssuer`.
 * Example output: `{ txn: "abc", issuer: "https://mcp.ept.ai/..." }`.
 * A different issuer returns null.
 */
export function readMcpControlLogin(
  search: string,
  allowedIssuer: string | null
): McpControlLogin | null {
  const params = new URLSearchParams(search);
  const txn = params.get('mcp_txn');
  const issuer = params.get('iss');
  if (!txn || !issuer || issuer !== allowedIssuer) {
    return null;
  }
  return { txn, issuer };
}

const _STORAGE_KEY = 'eptai_mcp_control_login';

/** Login path that still has the transaction after the IdP round trip. */
export function controlLoginPath(login: McpControlLogin): string {
  return `/login?mcp_txn=${encodeURIComponent(login.txn)}&iss=${encodeURIComponent(login.issuer)}`;
}

/** Keep the transaction in this tab. Auth0 comes back without the query. */
export function rememberMcpControlLogin(login: McpControlLogin): void {
  try {
    sessionStorage.setItem(_STORAGE_KEY, JSON.stringify(login));
  } catch {
    // The query string can still finish the login if it survives.
  }
}

/**
 * Read the transaction saved before the IdP redirect.
 *
 * Example output: `{ txn: "abc", issuer: "https://mcp.ept.ai/..." }` for app.ept.ai.
 * A different host returns null.
 */
export function peekMcpControlLogin(
  hostname: string = window.location.hostname
): McpControlLogin | null {
  try {
    const raw = sessionStorage.getItem(_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { txn?: unknown; issuer?: unknown };
    if (typeof parsed.txn !== 'string' || typeof parsed.issuer !== 'string') {
      return null;
    }
    if (parsed.issuer !== controlIssuerForHost(hostname)) {
      return null;
    }
    return { txn: parsed.txn, issuer: parsed.issuer };
  } catch {
    return null;
  }
}

/** Drop the transaction after the finish URL is opened. */
export function forgetMcpControlLogin(): void {
  try {
    sessionStorage.removeItem(_STORAGE_KEY);
  } catch {
    // The tab is leaving anyway.
  }
}

/**
 * Prove the signed-in person to mcp-tools and return its finish URL.
 *
 * Example input: the issuer, transaction id, and the app ID token.
 * Example output: `https://mcp.ept.ai/.../oauth/finish?ticket=...`
 */
export async function postMcpControlSession(
  issuer: string,
  txn: string,
  idToken: string
): Promise<string> {
  const response = await fetch(`${issuer}/oauth/session`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ txn, id_token: idToken }),
  });
  if (!response.ok) {
    throw new Error('Could not finish connecting.');
  }
  const body = (await response.json()) as { finish_url?: unknown };
  if (
    typeof body.finish_url !== 'string' ||
    !body.finish_url.startsWith(`${issuer}/oauth/finish?`)
  ) {
    throw new Error('Could not finish connecting.');
  }
  return body.finish_url;
}
